// Shopify owns discounts and prices. This control saves only the free-mat preference.
(() => {
  if (window.ysMatOfferInstalled) return;
  window.ysMatOfferInstalled = true;
  const planIds = new Set(['50434565505269', '50434565538037']);
  const attribute = 'yogaspace_free_mat';
  const cartRoot = window.Shopify?.routes?.root || '/';
  const originalFetch = window.fetch;
  let cart = null;
  let confirmedEnabled = document.querySelector('[data-ys-mat-checkbox]')?.checked ?? true;
  let saving = false;
  let refreshAfterSave = false;
  let requestId = 0;
  let refreshTimer;
  let errorMessage = '';

  function validCart(value) {
    return value && typeof value.currency === 'string' && Array.isArray(value.items)
      && (!value.attributes || (typeof value.attributes === 'object' && !Array.isArray(value.attributes)));
  }

  function acceptCart(nextCart) {
    if (!validCart(nextCart)) throw new Error('Invalid cart');
    cart = nextCart;
    confirmedEnabled = cart.attributes?.[attribute] !== 'false';
  }

  function renderOffer() {
    document.querySelectorAll('[data-ys-mat-offer]').forEach(offer => {
      const root = offer.closest('.yogaspace-product-main');
      const selectedId = root?.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
      offer.hidden = (cart?.currency || offer.dataset.currency) !== 'GBP'
        || (Boolean(selectedId) && !planIds.has(selectedId));
      const checkbox = offer.querySelector('[data-ys-mat-checkbox]');
      checkbox.checked = confirmedEnabled;
      checkbox.disabled = saving;
      offer.setAttribute('aria-busy', String(saving));
      offer.querySelector('[data-ys-mat-voucher]').dataset.checked = String(confirmedEnabled);
      offer.querySelector('[data-ys-mat-chooser]').hidden = !confirmedEnabled;
      const error = offer.querySelector('[data-ys-mat-error]');
      if (error) {
        error.textContent = errorMessage;
        error.hidden = !errorMessage;
      }
    });
  }

  async function readCart() {
    const response = await originalFetch.call(window, `${cartRoot}cart.js`, {
      cache: 'no-store', credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Cart unavailable');
    const value = await response.json();
    if (!validCart(value)) throw new Error('Invalid cart');
    return value;
  }

  async function refreshCart() {
    clearTimeout(refreshTimer);
    if (saving) { refreshAfterSave = true; return; }
    const currentRequest = ++requestId;
    try {
      const nextCart = await readCart();
      if (currentRequest !== requestId || saving) return;
      acceptCart(nextCart);
    } catch {
      // The server-rendered or last confirmed preference remains authoritative.
    }
    if (currentRequest === requestId) renderOffer();
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    ++requestId;
    if (saving) { refreshAfterSave = true; return; }
    refreshTimer = setTimeout(refreshCart, 40);
  }

  function notifyCartUpdated() {
    // The installed drawer keeps its own cart state outside the theme event bus.
    if (typeof window.CD_REFRESHCART === 'function') {
      try {
        Promise.resolve(window.CD_REFRESHCART()).catch(() => {});
      } catch { /* A drawer refresh must not undo a confirmed preference save. */ }
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'yogaspace-free-mat', cartData: cart });
    }
    document.dispatchEvent(new CustomEvent('yogaspace:cart-updated', { detail: { cart } }));
  }

  async function savePreference(enabled) {
    if (saving) return;
    saving = true;
    errorMessage = '';
    clearTimeout(refreshTimer);
    ++requestId;
    // Keep the confirmed state visible while the request is pending.
    renderOffer();
    let reconciled = false;
    try {
      const response = await originalFetch.call(window, `${cartRoot}cart/update.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // Updating one attribute merges it; no items, note or other attributes are sent.
        body: JSON.stringify({ attributes: { [attribute]: String(enabled) } }),
      });
      if (!response.ok) throw new Error('Cart update failed');
      acceptCart(await response.json());
      reconciled = true;
      if (confirmedEnabled !== enabled) errorMessage = 'Couldn’t save your choice. Please try again.';
    } catch {
      // A dropped response can follow a successful write. Read back before restoring.
      try { acceptCart(await readCart()); reconciled = true; } catch { /* Retain last confirmed state. */ }
      if (!reconciled || confirmedEnabled !== enabled) errorMessage = 'Couldn’t save your choice. Please try again.';
    } finally {
      saving = false;
      renderOffer();
      if (reconciled) notifyCartUpdated();
      if (refreshAfterSave) { refreshAfterSave = false; scheduleRefresh(); }
    }
  }

  function isCartWrite(url) {
    try {
      return /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(new URL(url, location.href).pathname);
    } catch { return false; }
  }

  // Retain cart-drawer integration without observing our own attribute request twice.
  window.fetch = function (...args) {
    const result = originalFetch.apply(this, args);
    const url = typeof args[0] === 'string' || args[0] instanceof URL ? args[0] : args[0]?.url;
    if (isCartWrite(url)) result.then(response => { if (response.ok) scheduleRefresh(); }, () => {});
    return result;
  };
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  const cartWrites = new WeakMap();
  XMLHttpRequest.prototype.open = function (method, url, ...args) {
    cartWrites.set(this, isCartWrite(url));
    return originalOpen.call(this, method, url, ...args);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (cartWrites.get(this)) this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) scheduleRefresh();
    }, { once: true });
    return originalSend.apply(this, args);
  };

  function initOffers() {
    document.querySelectorAll('[data-ys-mat-offer]').forEach(offer => {
      if (offer.dataset.ready) return;
      offer.dataset.ready = 'true';
      const checkbox = offer.querySelector('[data-ys-mat-checkbox]');
      checkbox.addEventListener('change', () => {
        if (!checkbox.disabled) savePreference(checkbox.checked);
      });
    });
    renderOffer();
  }
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      if (event?.source !== 'yogaspace-free-mat') scheduleRefresh();
    });
    subscribe(PUB_SUB_EVENTS.variantChange, renderOffer);
  }
  document.addEventListener('change', event => {
    if (event.target.matches('.yogaspace-product-main input[name="id"], .yogaspace-product-main input[type="radio"][name="Plan"]')) renderOffer();
  });
  window.addEventListener('pageshow', scheduleRefresh);
  window.addEventListener('focus', scheduleRefresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleRefresh(); });
  document.addEventListener('shopify:section:load', () => { initOffers(); scheduleRefresh(); });
  initOffers();
  refreshCart();
})();
